/**
 * Delete Webhook Confirmation Modal (September 2025)
 *
 * Confirmation dialog for webhook deletion with terminal-themed styling.
 *
 * Features:
 * - Destructive action confirmation
 * - Webhook details display
 * - Terminal-themed styling with Tailwind CSS
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

interface Webhook {
  id: Id<'webhooks'>;
  name: string;
  url: string;
  events: string[];
}

interface DeleteWebhookModalProps {
  isOpen: boolean;
  webhook: Webhook | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function DeleteWebhookModal({
  isOpen,
  webhook,
  onClose,
  onSuccess,
}: DeleteWebhookModalProps) {
  const convex = useConvex();
  const queryClient = useQueryClient();

  const deleteWebhookMutation = useMutation({
    mutationFn: async (webhookId: Id<'webhooks'>) => {
      return await convex.mutation(api.webhooks.mutations.deleteWebhook, {
        webhookId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    },
  });

  const handleDelete = async () => {
    if (!webhook) return;
    try {
      await deleteWebhookMutation.mutateAsync(webhook.id);
    } catch (error) {
      console.error('Failed to delete webhook:', error);
    }
  };

  if (!isOpen || !webhook) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-terminal-black/90 backdrop-blur-sm">
      <div className="terminal-box max-w-2xl w-full mx-4 p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-red-400 mb-2">
            [DELETE WEBHOOK]
          </h2>
          <p className="text-terminal-cyan text-sm">
            This action cannot be undone. The webhook will stop receiving events immediately.
          </p>
        </div>

        <div className="bg-terminal-black border-2 border-red-400 p-6 mb-6">
          <h3 className="text-red-400 font-bold mb-4">[WEBHOOK TO DELETE]</h3>
          <div className="space-y-2">
            <div>
              <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                Name
              </label>
              <div className="text-terminal-cyan text-lg font-semibold">
                {webhook.name}
              </div>
            </div>
            <div>
              <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                URL
              </label>
              <div className="text-terminal-cyan text-sm font-mono break-all">
                {webhook.url}
              </div>
            </div>
            <div>
              <label className="text-terminal-cyan/70 text-xs font-bold uppercase">
                Events
              </label>
              <div className="text-terminal-cyan text-sm">
                {webhook.events.length} event{webhook.events.length !== 1 ? 's' : ''} subscribed
              </div>
            </div>
          </div>
        </div>

        <div className="bg-red-900/20 border-2 border-red-400 p-4 mb-6">
          <h3 className="text-red-400 font-bold mb-2">[WARNING]</h3>
          <ul className="text-red-400/90 text-sm space-y-1 list-disc list-inside">
            <li>Webhook will be permanently deleted</li>
            <li>All delivery statistics will be lost</li>
            <li>Event notifications will stop immediately</li>
            <li>This action cannot be undone</li>
          </ul>
        </div>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={deleteWebhookMutation.isPending}
            className="flex-1 bg-transparent text-terminal-cyan font-bold py-3 px-6 border-2 border-terminal-cyan hover:bg-terminal-cyan hover:text-terminal-black transition-all duration-300 disabled:opacity-50"
          >
            CANCEL
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteWebhookMutation.isPending}
            className="flex-1 bg-red-400 text-terminal-black font-bold py-3 px-6 border-2 border-red-400 hover:bg-transparent hover:text-red-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleteWebhookMutation.isPending ? 'DELETING...' : 'DELETE WEBHOOK'}
          </button>
        </div>

        {deleteWebhookMutation.isError && (
          <div className="mt-4 bg-red-900/20 border-2 border-red-400 p-4">
            <p className="text-red-400 text-sm font-bold">
              [ERROR] Failed to delete webhook
            </p>
            <p className="text-red-400/70 text-xs mt-1">
              {(deleteWebhookMutation.error as Error)?.message || 'Unknown error occurred'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
